import { useRef, useEffect } from "react";
import Lenis from "lenis";
import { createLenis } from "../../lenis";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import type { JoinLayoutProps } from "../types";
import { WeddingProgram } from "../WeddingProgram";
import { DEMO_VIDEOS, DEMO_IMAGES, DEMO_FRAME_COUNT } from "../mock-data";
import { useCinematicScroll } from "./useCinematicScroll";
import { useScrollFrameSequence } from "../../useScrollFrameSequence";

gsap.registerPlugin(ScrollTrigger);

function formatDate(dateTime: string | null) {
  if (!dateTime) return { day: "--", month: "--", year: "----" };
  const d = new Date(dateTime);
  return {
    day: d.getDate().toString().padStart(2, "0"),
    month: d.toLocaleString("tr-TR", { month: "long" }).toUpperCase(),
    year: d.getFullYear().toString(),
    full: d.toLocaleString("tr-TR", { weekday: "long", day: "numeric", month: "long", year: "numeric" }),
    time: d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }),
  };
}

export function CinematicLayout({ formSlot, program, title, dateTime, location, description, slug, coverPhotoUrl }: JoinLayoutProps) {
  const lenisRef = useRef<Lenis | null>(null);
  const chapterVideoRef = useRef<HTMLElement | null>(null);
  const syncCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const date = formatDate(dateTime);

  // Apple-style frame sequence instead of <video>.currentTime scrubbing.
  useScrollFrameSequence({
    containerRef: chapterVideoRef,
    canvasRef: syncCanvasRef,
    source: { kind: "video", url: DEMO_VIDEOS.timelineScroll, count: DEMO_FRAME_COUNT },
    pinViewports: 3, // matches old "+=300%" pin
  });

  // Smooth scroll + GSAP sync
  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = createLenis({ duration: 1.4, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)) });
    lenis.scrollTo(0, { immediate: true });
    lenisRef.current = lenis;
    const ticker = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(ticker);
    gsap.ticker.lagSmoothing(0);
    lenis.on("scroll", ScrollTrigger.update);
    return () => {
      lenis.destroy();
      gsap.ticker.remove(ticker);
      ScrollTrigger.killAll();
    };
  }, []);

  useCinematicScroll();

  const quotes = [
    "İki kalbin tek bir hikâyesi…",
    "Sizi bu özel günümüze bekliyoruz.",
    "Sevincinizle taçlanacağız.",
  ];

  return (
    <main className="bg-black text-white overflow-x-hidden">
      {/* ── BÖLÜM 1: Hero Video ─────────────────────────────────────── */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/*
          AI VIDEO BRIEF — Hero ambient loop:
          "Slow-motion silhouette of bride and groom walking along beach at
          golden hour, telephoto lens. 8-15s seamless loop. Warm gold gradient."

          Placeholder: /demo/cinematic-hero.mp4 (Big Buck Bunny 10s — NOT thematic,
          shows the effect/architecture only)
        */}
        <video
          className="absolute inset-0 w-full h-full object-cover opacity-60"
          poster={coverPhotoUrl ?? undefined}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          onError={(e) => {
            // Video başarısız → poster image görünür kalır
            (e.currentTarget as HTMLVideoElement).style.display = "none";
          }}
        >
          {/* AV1 first — ~30% smaller, Chrome/Edge/FF prefer it. */}
          <source src={DEMO_VIDEOS.heroLoopAv1} type='video/mp4; codecs="av01.0.05M.08"' />
          <source src={DEMO_VIDEOS.heroLoop} type='video/mp4; codecs="avc1.42E01E"' />
        </video>
        {/* Fallback statik image (video yüklenemezse arka plan) */}
        {coverPhotoUrl && (
          <div
            className="absolute inset-0 -z-10 bg-cover bg-center"
            style={{ backgroundImage: `url(${coverPhotoUrl})` }}
          />
        )}
        {/* Dark vignette overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/80" />

        {/* Content */}
        <div className="relative z-10 text-center px-4">
          <p className="cin-subtitle text-xs tracking-[0.4em] text-white/60 mb-6 uppercase">
            Düğün Davetiyesi
          </p>
          <h1 className="cin-title font-serif text-5xl sm:text-7xl md:text-8xl font-light tracking-tight leading-none mb-8">
            {title}
          </h1>
          {dateTime && (
            <p className="cin-subtitle text-sm tracking-widest text-white/70 uppercase">
              {date.full} · {date.time}
            </p>
          )}
        </div>

        {/* Scroll indicator */}
        <div className="cin-scroll-indicator absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-60">
          <span className="text-xs tracking-widest uppercase text-white/50">Scroll</span>
          <div className="w-px h-12 bg-gradient-to-b from-white/50 to-transparent" />
        </div>

        {/* Letterbox bars */}
        <div className="absolute top-0 left-0 right-0 h-[6vh] bg-black pointer-events-none" />
        <div className="absolute bottom-0 left-0 right-0 h-[6vh] bg-black pointer-events-none" />
      </section>

      {/* ── BÖLÜM 2: Date Reveal (Chapter 1) ───────────────────────── */}
      <section className="cin-chapter-date relative min-h-screen flex items-center overflow-hidden bg-black">
        <div className="cin-date-bg absolute inset-0 bg-[#1a0a08]" style={{ clipPath: "inset(0 0% 0 0)" }} />

        <div className="relative z-10 w-full max-w-6xl mx-auto px-6 sm:px-12 grid md:grid-cols-2 gap-12 items-center py-24">
          {/* Big date numbers */}
          <div className="text-center md:text-left">
            <div className="cin-date-number text-[clamp(5rem,15vw,12rem)] font-serif font-light leading-none tabular-nums text-white/90">
              {date.day}
            </div>
            <div className="cin-date-label text-lg sm:text-2xl tracking-[0.3em] uppercase text-[#c9a96e] mt-2">
              {date.month}
            </div>
            <div className="cin-date-label text-4xl sm:text-6xl font-serif font-light text-white/50 mt-1">
              {date.year}
            </div>
          </div>

          {/* Location card */}
          <div className="cin-date-label space-y-4">
            <div className="w-12 h-px bg-[#c9a96e]" />
            <p className="text-xs tracking-[0.3em] uppercase text-white/40">Mekan</p>
            <p className="text-2xl sm:text-3xl font-serif font-light text-white leading-snug">
              {location ?? "Mekan Açıklanacak"}
            </p>
            <p className="text-sm tracking-[0.2em] uppercase text-white/40">
              {date.time && `Saat ${date.time}`}
            </p>
          </div>
        </div>
      </section>

      {/* ── BÖLÜM 3: Scroll-synced Video (Chapter 2) ────────────────── */}
      {/*
        AI VIDEO BRIEF — Scroll-synced timeline:
        "Cinematic montage 15-20s: couple meeting in cafe → city walk →
        beach proposal → wedding preparation. Slow-motion, warm filmic grade.
        NOT a loop — single forward timeline. Kullanıcı scroll ile sahne sahne ilerler."

        Placeholder: /demo/cinematic-timeline.mp4 (Sintel trailer — efekti gösterir,
        gerçek içerik AI ile üretilecek)
      */}
      <section
        ref={chapterVideoRef as React.RefObject<HTMLElement>}
        className="cin-chapter-video relative h-screen overflow-hidden bg-black"
      >
        <canvas
          ref={syncCanvasRef}
          className="cin-sync-canvas absolute inset-0 w-full h-full object-cover opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/20" />

        {/* Quote overlays — CSS opacity transitions controlled by ScrollTrigger */}
        {quotes.map((q, i) => (
          <div
            key={i}
            className="cin-video-quote absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-700 pointer-events-none"
          >
            <p className="text-2xl sm:text-4xl md:text-5xl font-serif font-light text-white text-center max-w-2xl px-8 leading-snug drop-shadow-2xl">
              "{q}"
            </p>
          </div>
        ))}

        {/* Progress bar */}
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/10">
          <div className="gsap-video-progress h-full bg-[#c9a96e] origin-left scale-x-0" />
        </div>
      </section>

      {/* ── BÖLÜM 4: Story (Chapter 3) ──────────────────────────────── */}
      {/* AI VIDEO BRIEF: Video gerekmez — blurred floral/bokeh background görseli. */}
      <section className="cin-chapter-story relative min-h-screen flex items-center bg-black py-24">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url(${DEMO_IMAGES.heroAlt})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(8px)",
          }}
        />
        <div className="absolute inset-0 bg-black/70" />

        <div className="relative z-10 max-w-3xl mx-auto px-6 sm:px-12 text-center">
          <div className="w-12 h-px bg-[#c9a96e] mx-auto mb-10" />
          <p className="cin-story-line text-xs tracking-[0.4em] uppercase text-white/40 mb-8">
            Hikayemiz
          </p>
          {description?.split(". ").filter(Boolean).map((sentence, i) => (
            <p
              key={i}
              className="cin-story-line text-xl sm:text-2xl font-serif font-light text-white/90 leading-relaxed mb-4"
            >
              {sentence.trim()}{sentence.trim().endsWith(".") ? "" : "."}
            </p>
          )) ?? (
            <p className="cin-story-line text-xl font-serif text-white/60">
              Hikaye yakında paylaşılacak.
            </p>
          )}
        </div>
      </section>

      {/* ── BÖLÜM 5: Venue (Chapter 4) ──────────────────────────────── */}
      <section className="cin-chapter-venue relative min-h-[70vh] flex items-center bg-[#0a0605] py-24">
        <div className="max-w-5xl mx-auto px-6 sm:px-12 w-full">
          <div className="grid sm:grid-cols-2 gap-6">
            {/* Date card */}
            <div className="cin-venue-card border border-white/10 rounded p-8 hover:border-[#c9a96e]/40 transition-colors">
              <p className="text-xs tracking-[0.3em] uppercase text-white/30 mb-4">Tarih & Saat</p>
              <p className="text-3xl font-serif font-light text-white">{date.full}</p>
              <p className="text-[#c9a96e] mt-2">{date.time}</p>
            </div>

            {/* Location card */}
            <div className="cin-venue-card border border-white/10 rounded p-8 hover:border-[#c9a96e]/40 transition-colors">
              <p className="text-xs tracking-[0.3em] uppercase text-white/30 mb-4">Mekan</p>
              <p className="text-3xl font-serif font-light text-white leading-snug">
                {location ?? "Mekan Açıklanacak"}
              </p>
              {location && (
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(location)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-4 text-xs tracking-widest uppercase text-[#c9a96e] hover:underline"
                >
                  Haritada Göster →
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── BÖLÜM 6: Registration Form (Outro) ──────────────────────── */}
      <section className="relative min-h-screen flex items-center justify-center bg-black py-24 px-4">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-black via-[#1a0a08]/80 to-black" />
        </div>
        <div className="cin-form-wrapper relative z-10 w-full max-w-md">
          <p className="text-center text-xs tracking-[0.4em] uppercase text-white/30 mb-4">
            Katılım
          </p>
          <h2 className="text-center text-3xl sm:text-4xl font-serif font-light text-white mb-8">
            Bize Katılın
          </h2>
          <WeddingProgram entries={program} className="mb-10" />
          {formSlot}
        </div>
      </section>
    </main>
  );
}
